import { useState, useCallback, useEffect, useRef, ChangeEvent } from "react";
import { FiAlertCircle } from "react-icons/fi";
import { showToast } from "utils/helpers";
import Modal from "newui/components/ui/Modal/Modal";
import { Button } from "newui/components/Button";
import { InputWithLabel, SelectOption, SelectWithLabel, TextAreaWithLabel } from "newui/components/ui/InputWithLabel";
import { ClarificationTypeSelect } from "newui/components/ui/CommonSelects";
import { useClarificationTypes } from "newui/store/common-types";
import FallbackImage from "newui/components/FallbackImage/FallbackImage";
import {
	useCreateClarification,
	useUpdateClarification,
} from "../../../../../hooks/usePanelScoringQueries";
import type { PanelClarification } from "../../../../../types/scoring.type";

interface SupplierOption {
	inviteId: number;
	supplierLogo: string | null;
	supplierName: string;
}

interface EvaluationClarificationModalProps {
	editingClarification?: PanelClarification | null;
	evalId: number;
	isOpen: boolean;
	/** Pre-select a supplier (e.g. the one currently being scored) */
	preSelectedInviteId?: number | null;
	rfxId: number;
	/** Show "Submit and Create Another" button */
	showSubmitAndAnother?: boolean;
	suppliers: SupplierOption[];
	onClose: () => void;
}

interface ClarificationFormState {
	clarification_text: string;
	clarification_type_id: number | "";
	relates_to: string;
	rfx_invite_id: number | "";
}

const stripHtml = (html: string): string => {
	if (!html) return "";
	const doc = new DOMParser().parseFromString(html, "text/html");
	return doc.body.textContent || "";
};

export const EvaluationClarificationModal: React.FC<EvaluationClarificationModalProps> = ({
	rfxId,
	evalId,
	isOpen,
	suppliers,
	editingClarification = null,
	preSelectedInviteId,
	showSubmitAndAnother = true,
	onClose,
}) => {
	const createMutation = useCreateClarification(rfxId, evalId);
	const updateMutation = useUpdateClarification(rfxId, evalId);
	const { values: clarificationTypeOptions } = useClarificationTypes();
	const defaultTypeId: number | "" = (clarificationTypeOptions?.[0]?.value as number) ?? "";

	const getInitialSupplier = useCallback((): number | "" => {
		if (preSelectedInviteId) return preSelectedInviteId;
		if (editingClarification) return editingClarification.rfx_invite_id;
		return suppliers.length > 0 ? suppliers[0].inviteId : "";
	}, [preSelectedInviteId, editingClarification, suppliers]);

	const [form, setForm] = useState<ClarificationFormState>({
		rfx_invite_id: getInitialSupplier(),
		clarification_type_id: editingClarification?.clarification_type_id ?? defaultTypeId,
		clarification_text: stripHtml(editingClarification?.clarification_text ?? ""),
		relates_to: editingClarification?.relates_to ?? "",
	});

	const [supplierLogo, setSupplierLogo] = useState<string | null>(() => {
		const sid = preSelectedInviteId ?? editingClarification?.rfx_invite_id ?? suppliers[0]?.inviteId;
		return suppliers.find((s) => s.inviteId === sid)?.supplierLogo ?? null;
	});

	const prevOpen = useRef(false);
	useEffect(() => {
		if (isOpen && !prevOpen.current) {
			const initialSupplier = getInitialSupplier();
			setForm({
				rfx_invite_id: initialSupplier,
				clarification_type_id: editingClarification?.clarification_type_id ?? defaultTypeId,
				clarification_text: stripHtml(editingClarification?.clarification_text ?? ""),
				relates_to: editingClarification?.relates_to ?? "",
			});
			setSupplierLogo(suppliers.find((s) => s.inviteId === initialSupplier)?.supplierLogo ?? null);
		}
		prevOpen.current = isOpen;
	}, [isOpen, editingClarification, defaultTypeId, getInitialSupplier, suppliers]);

	const resetForm = useCallback((keepSupplier = false) => {
		setForm((prev) => ({
			rfx_invite_id: keepSupplier ? prev.rfx_invite_id : getInitialSupplier(),
			clarification_type_id: defaultTypeId,
			clarification_text: "",
			relates_to: "",
		}));
	}, [defaultTypeId, getInitialSupplier]);

	const isFormValid = Boolean(form.rfx_invite_id && form.clarification_type_id && form.clarification_text.trim());
	const activeMutation = editingClarification ? updateMutation : createMutation;

	const handleSubmit = useCallback(() => {
		if (!isFormValid) return;
		if (editingClarification) {
			updateMutation.mutate(
				{
					clarificationId: editingClarification.id,
					payload: {
						rfx_invite_id: Number(form.rfx_invite_id),
						clarification_type_id: Number(form.clarification_type_id),
						clarification_text: form.clarification_text.trim(),
						relates_to: form.relates_to.trim() || null,
					},
				},
				{
					onSuccess: () => {
						showToast("Clarification updated", "Success", true);
						onClose();
					},
				}
			);
		} else {
			createMutation.mutate(
				{
					rfx_invite_id: Number(form.rfx_invite_id),
					clarification_type_id: Number(form.clarification_type_id),
					clarification_text: form.clarification_text.trim(),
					relates_to: form.relates_to.trim() || undefined,
				},
				{
					onSuccess: () => {
						showToast("Clarification created", "Success", true);
						onClose();
					},
				}
			);
		}
	}, [form, isFormValid, editingClarification, createMutation, updateMutation, onClose]);

	const handleSubmitAndAnother = useCallback(() => {
		if (!isFormValid) return;
		createMutation.mutate(
			{
				rfx_invite_id: Number(form.rfx_invite_id),
				clarification_type_id: Number(form.clarification_type_id),
				clarification_text: form.clarification_text.trim(),
				relates_to: form.relates_to.trim() || undefined,
			},
			{
				onSuccess: () => {
					showToast("Clarification created", "Success", true);
					resetForm(true);
					setTimeout(() => document.getElementById("clar-text")?.focus(), 100);
				},
			}
		);
	}, [form, isFormValid, createMutation, resetForm]);

	const onSupplierChanged = useCallback((e: SelectOption) => {
		setForm((prev) => ({ ...prev, rfx_invite_id: Number(e.value) }));
		setSupplierLogo(suppliers.find((s) => s.inviteId === Number(e.value))?.supplierLogo ?? null);
	}, [suppliers]);

	const onTypeChanged = useCallback((e: SelectOption) => {
		setForm((prev) => ({ ...prev, clarification_type_id: Number(e.value) }));
	}, []);

	const onQuestionChanged = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
		setForm((prev) => ({ ...prev, clarification_text: e.target.value }));
	}, []);

	const onRelatesChanged = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setForm((prev) => ({ ...prev, relates_to: e.target.value }));
	}, []);

	const supplierOptions = suppliers.map((s) => ({
		value: s.inviteId,
		label: s.supplierName,
	} as SelectOption));

	const formTitle = editingClarification ? "Edit Clarification" : "Raise Clarification";
	const submitLabel = activeMutation.isPending
		? "Saving..."
		: editingClarification ? "Save" : "Submit";

	const showSupplierSelect = !preSelectedInviteId || !!editingClarification;

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={formTitle}
			size="md"
			modalType="standard"
			iconType={editingClarification ? "edit" : "create"}
			closeOnEsc
			closeOnOverlayClick={false}
			trapFocus={false}
			{...(editingClarification || !showSubmitAndAnother
				? {
					primaryButton: {
						text: submitLabel,
						onClick: handleSubmit,
						disabled: !isFormValid || activeMutation.isPending,
					},
					secondaryButton: {
						text: "Cancel",
						onClick: onClose,
					},
				}
				: {
					customFooter: (
						<div className="flex w-full justify-between gap-2">
							<Button
								btntype="primary"
								size="sm"
								onClick={handleSubmitAndAnother}
								disabled={!isFormValid || createMutation.isPending}
							>
								Submit and Create Another
							</Button>
							<div className="flex gap-2">
								<Button btntype="secondary" size="sm" onClick={onClose}>
									Cancel
								</Button>
								<Button
									btntype="secondary"
									size="sm"
									onClick={handleSubmit}
									disabled={!isFormValid || createMutation.isPending}
								>
									{submitLabel}
								</Button>
							</div>
						</div>
					),
				}
			)}
		>
			<div className="space-y-4">
				{activeMutation.isError && (
					<div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
						<FiAlertCircle size={14} className="shrink-0 text-red-500" />
						<p className="text-xs text-red-700">
							{editingClarification
								? "Failed to update clarification. Please try again."
								: "Failed to create clarification. Please try again."
							}
						</p>
					</div>
				)}

				{showSupplierSelect ? (
					<div className="flex flex-row items-center gap-4">
						<FallbackImage src={supplierLogo} className="h-[50px] w-[50px] object-contain" />
						<SelectWithLabel
							id="clar-supplier"
							label="Supplier"
							onChange={onSupplierChanged}
							options={supplierOptions}
							value={form.rfx_invite_id as number}
							data-testid="clar-supplier-select"
							required
							className="w-full"
						/>
					</div>
				) : (
					<div className="flex flex-row items-center gap-4">
						<FallbackImage src={supplierLogo} className="h-[50px] w-[50px] object-contain" />
						<span className="text-sm font-medium text-gray-700">
							{suppliers.find((s) => s.inviteId === preSelectedInviteId)?.supplierName}
						</span>
					</div>
				)}

				<div>
					<ClarificationTypeSelect
						name="clarification_type_id"
						required
						value={(form.clarification_type_id || undefined) as number | undefined}
						onChange={onTypeChanged}
						data-testid="clar-type-select"
					/>
				</div>

				<div>
					<TextAreaWithLabel
						label="Clarification"
						id="clar-text"
						value={form.clarification_text}
						onChange={onQuestionChanged}
						placeholder="Enter your clarification request..."
						rows={4}
						maxLength={2000}
						required
					/>
				</div>

				<div>
					<InputWithLabel
						label="Relates To"
						name="relates_to"
						id="clar-relates"
						type="text"
						placeholder="e.g. Returnable Schedule Question 1.5 or Quote Item 3"
						value={form.relates_to}
						onChange={onRelatesChanged}
						maxLength={255}
						data-testid="clar-relates-input"
					/>
				</div>
			</div>
		</Modal>
	);
};
